import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Sub, Table } from '../../performance-operations/styles';
import { formatNumber, metricOf } from '../../performance-operations/config';
import { Kpi } from './DevelopmentPanels';
import { PIP_STATUSES } from '../config';

// Analiz E12 — PIP sekmesi (yalnız İK): M36 aktif PIP (tekil kişi ayrıca), M37 başarı oranı (kapanış penceresinde başarılı /
// başarılı + başarısız; iptal ve açık dışarıda), M38 iyileşme süresi (başarılı kapanan planlarda medyan takvim günü, n görünür) ve
// "İnceleme Bekleyen Düşük Performans" (taslak PIP — "PIP adayı" başlığı kullanılmaz). Durum dağılımı grafik + tablo.
const statusLabel = (t, s) => (s === 'draft'
  ? t('performance.developmentAnalytics.pip.pendingReview')
  : t(`performance.pip.status.${s}`, { defaultValue: s }));

export function PipKpis({ panel }) {
  const { t, i18n } = useTranslation();
  const d = panel.data;
  const common = { loading: panel.loading, error: panel.error, onRetry: panel.retry };
  const m38 = metricOf(d?.metrics, 'M38');
  return (
    <>
      <Kpi
        code="M36" title={t('performance.developmentAnalytics.pip.active')} metric={metricOf(d?.metrics, 'M36')} unit="count"
        sub={d ? t('performance.developmentAnalytics.pip.activePersons', { n: formatNumber(d.activePersons, i18n.language, 0) }) : null}
        {...common}
      />
      <Kpi
        code="M37" title={t('performance.developmentAnalytics.pip.successRate')} metric={metricOf(d?.metrics, 'M37')}
        sub={d ? t('performance.developmentAnalytics.pip.successRateSub', { cancelled: d.cancelled }) : null} {...common}
      />
      <Kpi
        code="M38" title={t('performance.developmentAnalytics.pip.recoveryDays')} metric={m38} unit="days"
        sub={m38 ? t('performance.developmentAnalytics.pip.recoveryDaysSub', { n: m38.sampleN ?? 0 }) : null} {...common}
      />
      <Kpi
        code="pendingReview" title={t('performance.developmentAnalytics.pip.pendingReview')}
        metric={d ? { value: d.statusCounts?.draft || 0 } : null} unit="count"
        sub={t('performance.developmentAnalytics.pip.pendingReviewSub')} {...common}
      />
    </>
  );
}

export function PipStatusPanel({ panel, onPickStatus }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const counts = panel.data?.statusCounts || {};
  const rows = PIP_STATUSES.map((s) => ({ key: s, label: statusLabel(t, s), count: counts[s] || 0 }));
  const empty = rows.every((r) => r.count === 0);
  return (
    <PanelCard
      panelKey="pipStatus" title={t('performance.developmentAnalytics.panel.pipStatus')} span={8}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={empty}
      emptyText={t('performance.developmentAnalytics.pip.empty')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead><tr><th>{t('performance.developmentAnalytics.col.status')}</th><th>{t('performance.developmentAnalytics.col.count')}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-row={r.key}>
                <td>{r.count > 0 ? <Clickable type="button" data-pick={r.key} onClick={() => onPickStatus(r.key)}>{r.label}</Clickable> : r.label}</td>
                <td>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: theme.metrics.font.size.xs }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Bar
                dataKey="count" name={t('performance.developmentAnalytics.col.count')} fill={theme.colors.primary.main} cursor="pointer"
                onClick={(d) => onPickStatus((d.payload || d).key)}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {panel.data?.windowFrom || panel.data?.windowTo
        ? <Sub data-note="window">{t('performance.developmentAnalytics.pip.windowNote')}</Sub>
        : <Sub data-note="window">{t('performance.developmentAnalytics.pip.allClosedNote')}</Sub>}
    </PanelCard>
  );
}

export function PipAttentionPanel({ panel, onPickStatus }) {
  const { t, i18n } = useTranslation();
  const a = panel.data?.attention || {};
  const n = (v) => formatNumber(v || 0, i18n.language, 0);
  return (
    <PanelCard
      panelKey="pipAttention" title={t('performance.developmentAnalytics.panel.attention')} span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
    >
      <Table>
        <tbody>
          <tr data-row="checkInDue">
            <td><Clickable type="button" onClick={() => onPickStatus('active')}>{t('performance.developmentAnalytics.pip.checkInDue')}</Clickable></td>
            <td>{n(a.checkInDue)}</td>
          </tr>
          <tr data-row="pastDue">
            <td>{t('performance.developmentAnalytics.pip.pastDue')}</td>
            <td>{n(a.pastDue)}</td>
          </tr>
          <tr data-row="closedWithoutGoalMarks">
            <td>{t('performance.developmentAnalytics.pip.closedWithoutGoalMarks')}</td>
            <td>{n(a.closedWithoutGoalMarks)}</td>
          </tr>
        </tbody>
      </Table>
      <Sub>{t('performance.developmentAnalytics.pip.disclaimer')}</Sub>
    </PanelCard>
  );
}
